import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SvgBreadCrumbArrow } from '../../../components/icons';
import StoplightSummary from './components/StoplightSummary';
import CustomerActivityTable from './components/CustomerActivityTable';
import AccountManagerFilter from './components/AccountManagerFilter';
import CustomerFilter from './components/CustomerFilter';
import {
  customerAPI,
  ActivityStatus,
  CustomerWithLoginStatus,
  Customer
} from '../../api/customer';

const MemberActivity: React.FC = () => {
  const [activitySummary, setActivitySummary] = useState<{
    active: number;
    warning: number;
    inactive: number;
    total: number;
  } | null>(null);
  const [customers, setCustomers] = useState<CustomerWithLoginStatus[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ActivityStatus>('active');
  const [showTable, setShowTable] = useState(false);
  const [selectedAccountManager, setSelectedAccountManager] = useState<string | null>(null);
  const [selectedAccountManagerName, setSelectedAccountManagerName] = useState<string>('All Account Managers');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData();
  }, [selectedAccountManager, selectedCustomer]);

  const fetchData = async () => {
    // Use different loading state depending on initial vs refresh
    if (!activitySummary) {
      setLoading(true);
    } else {
      setLoadingSummary(true);
    }
    setError(null);
    try {
      // Fetch optimized health summary from backend
      const searchQuery = selectedCustomer
        ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`.trim()
        : undefined;

      const summary = await customerAPI.getCustomerActivitySummary(
        selectedAccountManager || undefined,
        searchQuery
      );

      console.log('Health summary fetched:', summary);
      setActivitySummary(summary);
    } catch (err) {
      console.error('Error fetching member activity summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch member activity summary');
    } finally {
      setLoading(false);
      setLoadingSummary(false);
    }
  };

  const fetchCustomersPage = async (page: number, filter: ActivityStatus) => {
    setLoadingCustomers(true);
    try {
      const searchQuery = selectedCustomer
        ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`.trim()
        : undefined;

      const response = await customerAPI.getCustomersWithActivityStatus({
        page,
        perPage: 20,
        activityStatus: filter,
        accountManagerHubspotId: selectedAccountManager || undefined,
        searchQuery,
      });

      setCustomers(response.customers);
      setCurrentPage(response.page);
      setTotalPages(response.pages);
      setTotalCount(response.total);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const refreshData = async () => {
    // Refresh health summary
    await fetchData();

    // If table is showing, refresh current page
    if (showTable) {
      await fetchCustomersPage(currentPage, activeFilter);
    }
  };

  const handleStoplightClick = async (filter: ActivityStatus) => {
    setActiveFilter(filter);
    setShowTable(true);
    setCurrentPage(0);

    // Fetch first page of filtered customers
    await fetchCustomersPage(0, filter);

    // Scroll to table
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handlePageChange = async (page: number) => {
    await fetchCustomersPage(page, activeFilter);

    // Scroll to top of table
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleAccountManagerChange = async (hubspotId: string | null, name: string) => {
    setSelectedAccountManager(hubspotId);
    setSelectedAccountManagerName(name);
    // Don't hide the table - just refresh the data
    // If table was showing, refresh it with the new filter
    if (showTable) {
      await fetchCustomersPage(0, activeFilter);
    }
    setCurrentPage(0);
    // fetchData will be triggered by the useEffect when selectedAccountManager changes
  };

  const handleCustomerChange = async (customer: Customer | null) => {
    setSelectedCustomer(customer);
    // If table was showing, refresh it with the new filter
    if (showTable) {
      await fetchCustomersPage(0, activeFilter);
    }
    setCurrentPage(0);
    // fetchData will be triggered by the useEffect when selectedCustomer changes
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Link to="/" className="text-gray-600 hover:text-gray-800">
            Home
          </Link>
          <SvgBreadCrumbArrow className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">Member Activity</span>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-medium text-lg mb-2">Error Loading Data</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Link to="/" className="text-gray-600 hover:text-gray-800">
            Home
          </Link>
          <SvgBreadCrumbArrow className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">Member Activity</span>
        </div>

        <div className="flex flex-col items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading activity summary...</p>
        </div>
      </div>
    );
  }

  if (!activitySummary) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-center gap-2 mb-6">
        <Link to="/" className="text-gray-600 hover:text-gray-800">
          Home
        </Link>
        <SvgBreadCrumbArrow className="w-4 h-4 text-gray-400" />
        <span className="text-gray-900 font-medium">Member Activity</span>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Member Activity Dashboard</h1>
            <p className="text-lg text-gray-600">
              Track active customer engagement and identify users needing attention
            </p>
            <div className="mt-2 flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                Analyzing {activitySummary.total.toLocaleString()} active customers
              </span>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                STATUS: ACTIVE
              </span>
              {selectedAccountManager && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                  {selectedAccountManagerName}
                </span>
              )}
              {selectedCustomer && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                  {selectedCustomer.firstName} {selectedCustomer.lastName}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={refreshData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            disabled={loading}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap gap-6 items-center">
          <AccountManagerFilter
            selectedAccountManager={selectedAccountManager}
            onAccountManagerChange={handleAccountManagerChange}
            isFiltering={loadingSummary}
          />
          <CustomerFilter
            selectedCustomer={selectedCustomer}
            onCustomerChange={handleCustomerChange}
            isFiltering={loadingSummary}
          />
        </div>
      </div>

      <div className="space-y-6">
        <StoplightSummary
          summary={activitySummary}
          onFilterClick={handleStoplightClick}
          loading={loadingSummary}
        />

        <div ref={tableRef}>
          {showTable ? (
            <CustomerActivityTable
              customers={customers}
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loadingCustomers}
              activityFilter={activeFilter}
              onPageChange={handlePageChange}
            />
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-center py-12">
                <div className="text-4xl mb-4">👆</div>
                <p className="text-gray-600 text-lg font-medium mb-2">
                  Click on a category above to view detailed customer information
                </p>
                <p className="text-sm text-gray-500">
                  Customers are loaded 20 per page for optimal performance
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberActivity;